'use client';
import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import {
  ShieldCheck,
  Phone,
  DollarSign,
  FileText,
  ChevronRight,
  CheckCircle,
  RefreshCw,
  Truck,
} from 'lucide-react';
import { useSiteSettings } from '@/components/SiteSettingsProvider';

export default function RefundPolicyPage() {
  const { phoneNumber, showPhoneNumber } = useSiteSettings();
  return (
    <div className="min-h-screen font-sans bg-slate-50 text-slate-800 flex flex-col">
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <div className="relative pt-[120px] pb-20 bg-slate-950 overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-slate-950 z-0" />
        <div className="absolute top-[-100px] right-[-10%] w-[500px] h-[500px] rounded-full bg-accent/5 blur-3xl pointer-events-none z-0" />

        <div className="relative z-10 max-w-[1170px] mx-auto px-6 w-full text-left">
          <div
            style={{ height: '30px' }}
            className="flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-1 rounded-full text-[10px] font-black text-accent uppercase tracking-widest mb-4 w-fit backdrop-blur-md"
          >
            REIMBURSEMENTS
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight leading-none mb-4">
            Refund{' '}
            <span className="bg-gradient-to-r from-accent via-accent-light to-blue-500 bg-clip-text text-transparent">
              Policy
            </span>
          </h1>
          <p className="text-slate-400 text-xs md:text-sm font-semibold max-w-2xl leading-relaxed">
            Understand booking requirements, cancellation guidelines, and fees for domestic and
            international ticket exchanges.
          </p>
        </div>
      </div>

      {/* Main Grid Content */}
      <div className="flex-grow max-w-[1170px] mx-auto px-6 py-16 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Main refund terms (Left) */}
          <div className="lg:col-span-8 text-left space-y-8 animate-fadeInUp">
            {/* Booking Requirements */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider flex items-center gap-2">
                <FileText className="w-5.5 h-5.5 text-accent" />
                1. General Booking Requirements
              </h2>

              <ul className="space-y-4">
                {[
                  'Most airline tickets are non-refundable, non-changeable and non-cancellable. In limited instances, an airline may allow a ticket to be changed for a fee, plus the increased cost of the new ticket.',
                  'All reservations are subject to availability at the time of booking. At least one adult must accompany children below the age of 18 yrs. Children 12 yrs & above are considered adults for pricing purposes.',
                  'All tickets are subject to specific rules and regulations imposed by the individual airlines which are subject to change at any time.',
                  'Reservations are not ticketed or confirmed until the airline has issued a ticket number. A confirming e-mail will be sent indicating that a ticket has been issued. If you do not receive it, call +1 425-981-5705 or email support@faremaps.com.',
                  'Name changes are not permitted once the reservation has been confirmed. Once issued, a ticket may not be reassigned to a different passenger or airline.',
                  'Meal and seat preferences will be sent to the airline, but cannot be guaranteed. Service fees charged for making special requests are non-refundable. Seat selections may be amended by airlines without prior notice.',
                  'Baggage and Other Ancillary Airline Fees: The airlines may require you to pay additional fees at the airport. Each airline should be consulted for complete information about its baggage allowances and ancillary charges.',
                ].map((req, i) => (
                  <li
                    key={i}
                    className="flex gap-3.5 items-start text-slate-600 text-xs font-semibold leading-relaxed"
                  >
                    <span className="w-5.5 h-5.5 rounded-xl bg-slate-50 border border-slate-200/80 flex items-center justify-center text-slate-400 text-[10.5px] font-black flex-shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Cancel and Refund */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider flex items-center gap-2">
                <DollarSign className="w-5.5 h-5.5 text-accent" />
                2. Cancel and Refund Terms
              </h2>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                Most airline tickets are completely non-refundable and non-transferable. In cases
                where the airline does allow cancellations, a credit may be valid towards ticket
                purchase in future with travel on flights of the same airline. Fare difference (if
                any) and the applicable penalty will be added in such cases. The time constraints
                and expiration date of such credit should be discussed beforehand with our customer
                service agent.
              </p>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                All such bookings that allow for cancellation must be cancelled prior to the
                scheduled departure time of the flight by calling our customer service
                representative. We do not guarantee any cancellation.
              </p>

              <div className="p-5 bg-blue-500/5 border border-blue-500/10 rounded-2xl">
                <h4 className="text-blue-800 text-xs font-black uppercase tracking-wider mb-1">
                  Cancellation processing fee
                </h4>
                <p className="text-blue-800/80 text-xs font-semibold leading-relaxed">
                  At the time of booking cancellation, a cancellation fee of{' '}
                  <strong>$50 per ticket</strong> is payable to us. This fee is retained by
                  Faremaps. The credit available must be used within a certain time period and date
                  or you will lose the credit amount.
                </p>
              </div>
            </div>

            {/* Exchange fees table */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider flex items-center gap-2">
                <RefreshCw className="w-5.5 h-5.5 text-accent animate-spin-slow" />
                3. Exchange & Rebooking Service Fees
              </h2>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed mb-4">
                For itineraries where the airline permits changes, a change/exchange fee is charged
                by Faremaps.com, along with the fare difference if any and airline penalties:
              </p>

              <div className="border border-slate-200/80 rounded-2xl overflow-hidden shadow-sm">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-slate-800 font-black uppercase tracking-wider">
                      <th className="p-4">Itinerary Type</th>
                      <th className="p-4">Departure Timeframe</th>
                      <th className="p-4 text-right">Faremaps Service Fee</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-600 font-semibold">
                    <tr className="border-b border-slate-100">
                      <td className="p-4">Domestic Travel</td>
                      <td className="p-4">More than 7 days away</td>
                      <td className="p-4 text-right text-accent font-black">Up to $100</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="p-4">Domestic Travel</td>
                      <td className="p-4">Within 7 days away</td>
                      <td className="p-4 text-right text-accent font-black">$150</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="p-4">International Travel</td>
                      <td className="p-4">More than 7 days away</td>
                      <td className="p-4 text-right text-accent font-black">Up to $200</td>
                    </tr>
                    <tr>
                      <td className="p-4">International Travel</td>
                      <td className="p-4">Within 7 days away</td>
                      <td className="p-4 text-right text-accent font-black">$300</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Paper tickets */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider flex items-center gap-2">
                <Truck className="w-5.5 h-5.5 text-accent" />
                4. Paper Tickets and Shipping Fees
              </h2>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                Service fees apply to both e-tickets and paper tickets. Most transactions can be
                fulfilled using e-tickets. If an airline does not offer e-ticketing or if you
                request paper tickets, a shipping fee will be assessed:
              </p>

              <ul className="space-y-3.5 text-slate-600 text-xs font-semibold leading-relaxed">
                <li className="flex gap-2 items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 flex-shrink-0" />
                  <strong>Within United States:</strong> $25.00 USD per transaction for second
                  business day delivery.
                </li>
                <li className="flex gap-2 items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 flex-shrink-0" />
                  <strong>International Shipments (Canada, Alaska, Hawaii, Puerto Rico):</strong> A
                  minimum of $45.00 USD per transaction will be charged.
                </li>
                <li className="flex gap-2 items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 flex-shrink-0" />
                  <strong>P.O. Boxes:</strong> FedEx cannot ship paper tickets to a PO Box or APO
                  Box. You must provide us with a physical shipping address by emailing
                  support@faremaps.com.
                </li>
              </ul>
            </div>

            {/* Chargebacks */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider">
                5. Chargebacks and Credit Card Disputes
              </h2>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                The customer agrees not to dispute the processing fee, airline ticket charge or fare
                adjustment if Faremaps.com and Faremaps have abided by the terms and conditions of
                the airlines and this agreement.
              </p>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed font-bold">
                If the customer is questioning any charge on their credit card, please call {phoneNumber} or send an email to the Customer Service (support@faremaps.com) before
                disputing the charge with the credit card company.
              </p>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                If the customer attempts a chargeback on a non-refundable airline ticket, service
                fee or a fare adjustment that meets the terms and conditions of the airline and this
                agreement; the customer agrees to refund the full amount of the chargeback plus a
                50% penalty for the inconvenience and accounting fee.
              </p>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed text-red-600 font-bold">
                In cases where the customer attempts a fraudulent chargeback, the customer will be
                responsible for legal and collection costs in order to recoup the potential loss
                imposed by the customer.
              </p>
            </div>
          </div>

          {/* Sidebar widget (Right) */}
          <div className="lg:col-span-4 sticky top-[110px] space-y-6">
            {/* VIP Desk Card */}
            {showPhoneNumber && (
              <div className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm text-left space-y-6">
                <div>
                  <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest mb-4 pb-2 border-b border-slate-100 block">
                    Refund Assistance
                  </h4>
                  <p className="text-slate-500 text-xs font-semibold leading-relaxed mb-4">
                    For rebookings, cancellation status, or billing queries, contact our support team.
                  </p>

                  <a
                    href={`tel:${phoneNumber}`}
                    className="flex items-center gap-3.5 bg-slate-950 hover:bg-slate-900 border border-white/10 rounded-2xl p-4 shadow-premium transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] group"
                  >
                    <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-blue-600 shadow-glow">
                      <span className="absolute inset-0 rounded-xl bg-accent animate-ping opacity-20"></span>
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                        TOLL-FREE 24/7
                      </div>
                      <div className="text-sm font-black text-white mt-1 tracking-tight leading-none group-hover:text-accent-light transition-colors">
                        {phoneNumber}
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            )}

              <div>
                <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest mb-4 pb-2 border-b border-slate-100 block">
                  Charge Assurance
                </h4>
                <div className="flex items-start gap-3 bg-slate-50 border border-slate-200/60 p-4 rounded-2xl">
                  <ShieldCheck className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <h5 className="text-slate-800 text-xs font-bold leading-tight">
                      Verified Transactions
                    </h5>
                    <p className="text-slate-400 text-[10px] font-semibold mt-1 leading-normal">
                      Full transparency in billing summaries and secure credit card checkouts.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


      <Footer />
    </div>
  );
}
